import { PlantTagRecord } from "@/lib/algolia/plantTags";

type SpecificationType = "string" | "boolean" | "temperature";

interface SpecificationDefinition {
  id: keyof PlantTagRecord;
  customLabel?: string;
  type: SpecificationType;
}

export interface SpecificationItemDetails {
  id: string;
  label: string;
  value: string;
}

const formatSpecificationLabel = (id: string) => id.replace(/_/g, " ");

const possibleSpecifications: SpecificationDefinition[] = [
  { id: "Type", type: "string" },
  { id: "Family", type: "string" },
  { id: "Variety", type: "string" },
  { id: "Colour", type: "string" },
  { id: "Shape", type: "string" },
  { id: "Style", type: "string" },
  { id: "Trunk_Shape", type: "string" },
  { id: "Leaf_Shape", type: "string" },
  { id: "Height", type: "string" },
  { id: "Width", type: "string" },
  { id: "Depth", type: "string" },
  { id: "Length", type: "string" },
  { id: "Diameter", type: "string" },
  { id: "Opening", type: "string" },
  { id: "Volume", type: "string" },
  { id: "Weight", type: "string" },
  { id: "Required_Temperature", type: "temperature", customLabel: "Req. Temp" },
  { id: "Required_Water_Level", type: "string", customLabel: "Req. Water level" },
  { id: "Required_Light_Level", type: "string", customLabel: "Req. Light level" },
  //{ id: "Fire_Retardent", type: "boolean" },
  //{ id: "UV_resistant", type: "boolean" },
  //{ id: "Water_resistant", type: "boolean" },
  { id: "With_Flower", type: "boolean" },
];

export const buildSpecificationItems = (record: PlantTagRecord): SpecificationItemDetails[] => {
  return possibleSpecifications.reduce<SpecificationItemDetails[]>((items, specification) => {
    const rawValue = record[specification.id] as unknown;

    if (rawValue === undefined || rawValue === null) {
      return items;
    }

    const label = specification.customLabel ?? formatSpecificationLabel(String(specification.id));

    if (specification.type === "string") {
      const stringValue = String(rawValue).trim();

      if (stringValue === "" || stringValue === "0") {
        return items;
      }

      items.push({ id: String(specification.id), label, value: stringValue });
      return items;
    }

    if (specification.type === "temperature") {
      items.push({ id: String(specification.id), label, value: `${rawValue}°C` });
      return items;
    }

    if (typeof rawValue === "string") {
      const trimmedValue = rawValue.trim();

      if (trimmedValue === "") {
        return items;
      }

      const booleanString = trimmedValue.toLowerCase();

      if (booleanString === "true" || booleanString === "false") {
        items.push({ id: String(specification.id), label, value: booleanString });
        return items;
      }

      const numericValue = Number(trimmedValue);

      if (Number.isNaN(numericValue)) {
        return items;
      }

      items.push({
        id: String(specification.id),
        label,
        value: numericValue === 1 ? "True" : "False",
      });

      return items;
    }

    if (typeof rawValue === "number") {
      items.push({
        id: String(specification.id),
        label,
        value: rawValue === 1 ? "True" : "False",
      });

      return items;
    }

    if (typeof rawValue === "boolean") {
      items.push({
        id: String(specification.id),
        label,
        value: rawValue ? "True" : "False",
      });

      return items;
    }

    return items;
  }, []);
};
